// React
import { useState } from "react"

// Animations
import { AnimatePresence, motion } from "@/lib/animations"

// UI
import { cn } from "@/lib/utils"
import { StatusMessage } from "./StatusMessage"

/**
 * ProjectTimeline
 */
export const ProjectTimeline = () => {
	// Data
	const list = [
		{
			id: 1,
			title: "Funding Secured",
			description:
				"Initial investment funding secured for solar project development",
			daysAgo: 180,
			status: "completed",
		},
		{
			id: 2,
			title: "Site Survey Completed",
			description:
				"Technical and environmental site assessment completed",
			daysAgo: 150,
			status: "completed",
		},
		{
			id: 3,
			title: "Permits Approved",
			description:
				"All necessary construction and grid connection permits obtained",
			daysAgo: 120,
			status: "completed",
		},
		{
			id: 4,
			title: "Equipment Procurement",
			description: "Solar panels, inverters and mounting systems ordered",
			daysAgo: 90,
			status: "in_progress",
		},
		{
			id: 5,
			title: "Construction Started",
			description: "Ground preparation and foundation work initiated",
			daysAgo: 60,
			status: "pending",
		},
		{
			id: 6,
			title: "Installation Complete",
			description: "Solar panels and electrical systems fully installed",
			daysAgo: 30,
			status: "pending",
		},
		{
			id: 7,
			title: "Production Started",
			description:
				"Grid connection established and power generation commenced",
			daysAgo: 0,
			status: "pending",
		},
	]

	return (
		<div className="grid-cols-4 gap-4 lg:grid">
			<ul className="col-span-3 space-y-6 lg:flex lg:space-y-0">
				{list.map((item, index) => (
					<ListItem
						index={index}
						item={item}
						listLength={list.length}
						key={item.id}
					/>
				))}
			</ul>
			<div className="col-span-1 mt-4 lg:mt-0">
				<img
					className="h-auto w-full rounded-md lg:rounded-lg"
					src="https://images.unsplash.com/photo-1623696613585-7319e61a5659?ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&ixlib=rb-1.2.1&auto=format&fit=crop&w=1047&q=80"
					alt=""
				/>
			</div>
		</div>
	)
}

interface ListItemProps {
	index: number
	listLength: number
	item: {
		id: number
		title: string
		description: string
		daysAgo: number
		status: string
	}
}

const ListItem = ({ index, item, listLength }: ListItemProps) => {
	const [isOpen, set] = useState(false)

	return (
		<motion.li className="relative" layout>
			{index < listLength - 1 ? (
				<Divider
					variant={
						item.status === "completed" ? "active" : "inactive"
					}
				/>
			) : null}
			<AnimatePresence>
				<motion.div
					className="flex items-start space-x-3 lg:block lg:space-x-0"
					layout
				>
					<IconBubble
						number={index + 1}
						variant={
							item.status === "completed"
								? "active"
								: item.status === "in_progress"
									? "active"
									: "inactive"
						}
					/>
					<motion.div>
						<motion.button
							layout
							className="timeline-item-content mb-3 flex-1 pt-1.5 text-left text-sm text-gray-700 outline-0 lg:pr-4 lg:pt-2 2xl:pr-8"
							onClick={() => set(!isOpen)}
						>
							<StatusMessage content={item.title} />{" "}
						</motion.button>
						{isOpen && (
							<motion.p
								layout
								exit={{ height: 0 }}
								initial={{ height: 0 }}
								animate={{ height: "auto" }}
								className="overflow-hidden text-xs text-gray-500 lg:text-sm"
							>
								{item.description}
							</motion.p>
						)}
					</motion.div>
				</motion.div>
			</AnimatePresence>
		</motion.li>
	)
}

const Divider = ({ variant }: { variant: "active" | "inactive" }) => (
	<span
		className={cn(
			variant === "active" ? "bg-black" : "bg-gray-200",
			"absolute -bottom-4 left-4 top-10 -ml-px w-0.5 lg:bottom-0 lg:left-10 lg:right-0 lg:top-4 lg:-mt-px lg:ml-0 lg:h-0.5 lg:w-full",
		)}
		aria-hidden="true"
	/>
)

const IconBubble = ({
	number,
	variant,
}: {
	number: number
	variant: "active" | "inactive"
}) => (
	<motion.div
		layout
		className={cn(
			variant === "active"
				? "bg-primary-500 text-dark"
				: "bg-gray-100 text-gray-700",
			"flex h-8 w-8 items-center justify-center rounded-full ring-8 ring-white",
		)}
	>
		{number}
	</motion.div>
)
